"use client";
import { create } from "zustand";
import {
  devtools,
  subscribeWithSelector,
  DevtoolsOptions,
} from "zustand/middleware";
import {
  CurrentChatStatus,
  HeaderType,
  IFeMessage,
  ImServiceChannel,
  Message,
  MessageSender,
  MessageStatus,
  MessageStore,
  MessageStoreCreator,
  MessageType,
  RobotMessageType,
  SocketConnectionStatus,
  SocketErrorInfo,
  RobotMessage,
} from "./types";
import {
  formatMsgList,
  getCurrentChatStatus,
  handleIncomingMessage,
  handleLineMsg,
  isForbiddenMsg,
} from "./formatMessage";
import { readMsgManager } from "./readMsgManager";
import { historyMsgManager } from "./historyMsgManager";
import { quotedMsgManager } from "./quoted";
import { ActiveConversation } from "../api/message";
import { ReportScene, reportScene } from "../services/DataReporter";
import { detectOSType } from "../utils/device";
import { getLang } from "../locales";

const createMessageStore: MessageStoreCreator = (set, get) => {
  // 已读消息
  const readMsgStore = readMsgManager({ set, get });
  // 引用消息
  const quotedMsgStore = quotedMsgManager({ set, get });
  // 历史消息
  const historyMsgStore = historyMsgManager({ set, get });

  return {
    ...readMsgStore,
    ...historyMsgStore,
    ...quotedMsgStore,
    // 初始状态
    msgList: [],
    isConnected: SocketConnectionStatus.DISCONNECTED,
    chatStatus: null,
    imServiceChannel: ImServiceChannel.Robot,
    conversationId: "",
    chatBanner: null,
    historyLoaded: false,
    // 用户是否已经发送过第一条消息
    hasUserSentFirstMessage: false,
    // AI 打字机效果是否结束
    aiTypingEnd: true,
    // 全局 SSE 停止函数
    globalStopStreaming: null,
    // AI SSE 流是否正在进行中
    isAIStreaming: false,
    reEditContent: null,

    setReEditContent: (content) => set({ reEditContent: content }),

    // 添加用户发送的消息
    addOutgoingMessage: (message: Message) => {
      const newMessage: Message = { ...message, isNewMsg: true };
      reportScene(
        ReportScene.USER_MESSAGE_SEND,
        JSON.stringify({
          clientId: message.clientId,
          device: detectOSType(),
          lang: getLang(),
        }),
        true,
      );
      set((state) => {
        const { chatStatus, msgList } = state;
        // 用户第一次发送消息，标记 flag
        const shouldSetFirst = !state.hasUserSentFirstMessage;
        if (chatStatus === CurrentChatStatus.LineUp) {
          const { appendMsg, excludeLineMsgList } = handleLineMsg(
            msgList,
            newMessage,
          );
          return {
            msgList: [...excludeLineMsgList, ...appendMsg],
            ...(shouldSetFirst ? { hasUserSentFirstMessage: true } : {}),
          };
        }
        return {
          msgList: [...msgList, newMessage],
          ...(shouldSetFirst ? { hasUserSentFirstMessage: true } : {}),
        };
      });

      return newMessage;
    },

    setCommonMessage: (message: Message) => {
      const newMessage: Message = { ...message };
      if (message.conversationId) {
        set(() => ({
          conversationId: message.conversationId,
        }));
      }

      return newMessage;
    },

    // 接收服务端推送的消息
    addIncomingMessage: (message) => {
      const { msgList, chatStatus } = get();
      const valid = handleIncomingMessage(message, msgList);
      if (!valid) {
        return;
      }
      // 处理新消息
      let handleMsgList = formatMsgList(msgList, message);
      const currentChatStatus = getCurrentChatStatus(message) || chatStatus;
      set((state) => {
        // 兜底保证确定是非用户消息
        const newUnreadCount =
          message.senderType !== MessageSender.USER
            ? state.unreadCount + 1
            : state.unreadCount;
        const unreadCountObject = get().bottomReached
          ? { unreadCount: state.unreadCount }
          : { unreadCount: newUnreadCount };

        if (chatStatus === CurrentChatStatus.LineUp) {
          // 当排队时，有消息输入，则依然将排队消息置于末尾（多端同时输入场景）
          const { appendMsg, excludeLineMsgList } = handleLineMsg(
            msgList,
            message,
          );
          handleMsgList = [...excludeLineMsgList, ...appendMsg];
        }
        return {
          msgList: handleMsgList,
          chatStatus: currentChatStatus,
          ...unreadCountObject,
        };
      });
    },

    // 更新消息状态
    updateMessageStatus: (clientId, opts: IFeMessage, serverId) => {
      set((state) => {
        const msgList = [...state.msgList];
        const { status } = opts;
        let messageIndex = 0;
        if (!clientId && serverId) {
          // 如果clientId为空字符串，则证明，期望更新来源于服务端的数据(注意，以下逻辑只针对新消息)
          messageIndex = msgList.findIndex((msg) => msg.id === serverId);
        } else {
          // 更新用户发送的消息
          messageIndex = msgList.findIndex((msg) => msg.clientId === clientId);
        }
        if (messageIndex !== -1) {
          const updatedMessage = { ...msgList[messageIndex], ...opts };

          if (serverId && status === MessageStatus.Success) {
            updatedMessage.id = serverId;
          }

          if (isForbiddenMsg(updatedMessage)) {
            updatedMessage.status = MessageStatus.Failed;
          }
          msgList[messageIndex] = updatedMessage;
        }
        return { msgList };
      });
    },

    // 处理发送结果
    handleSendResult: (clientId, success) => {
      set((state) => {
        const msgList = [...state.msgList];
        const messageIndex = msgList.findIndex(
          (msg) => msg.clientId === clientId,
        );

        if (messageIndex !== -1) {
          // 确定状态值，确保类型正确
          const status: MessageStatus = success
            ? MessageStatus.Success
            : MessageStatus.Failed;

          // 创建更新后的消息对象
          const updatedMessage: Message = {
            ...msgList[messageIndex],
            status,
          };

          msgList[messageIndex] = updatedMessage;
        }

        return { msgList };
      });
    },

    // 重试发送消息
    retrySendMessage: (message) => {
      set((state) => {
        const msgList = [...state.msgList];
        const messageIndex = msgList.findIndex(
          (msg) =>
            msg.clientId === message.clientId ||
            (msg.id === message.id && message.id),
        );
        if (messageIndex !== -1) {
          // 创建更新后的消息对象
          const updatedMessage: Message = {
            ...msgList[messageIndex],
            status: MessageStatus.Sending,
            retryCount: (msgList[messageIndex].retryCount || 0) + 1,
          };

          msgList[messageIndex] = updatedMessage;
        }

        return { msgList };
      });
    },

    setCurrentChatStatus: (chatStatus) => {
      if (
        chatStatus === CurrentChatStatus.LineUp ||
        chatStatus === CurrentChatStatus.Online
      ) {
        set({ chatStatus, imServiceChannel: ImServiceChannel.Human });
        return;
      }
      if (chatStatus === CurrentChatStatus.ChatEnd) {
        set({ chatStatus, imServiceChannel: ImServiceChannel.Robot });
        return;
      }
      set({ chatStatus });
    },

    setImServiceChannel: (imServiceChannel) => {
      set({ imServiceChannel });
    },

    // 设置连接状态
    setConnectionStatus: (isConnected) => {
      set({ isConnected });
    },

    // 撤回消息方法
    withdrawMessage: (messageId) => {
      set((state) => {
        const { quotedMsg, setQuotedMsg } = state;
        if (quotedMsg?.id === messageId) {
          setQuotedMsg({
            ...quotedMsg,
            content: JSON.stringify({ text: "" }),
            hasRedrawn: true,
          });
        }
        return {
          historyMsgList: state.historyMsgList.map((msg) => {
            if (msg.quoteMessage && msg.quoteMessage.id === messageId) {
              msg.quoteMessage.content = JSON.stringify({ text: "" });
            }
            return msg.id === messageId
              ? {
                  recallTime: Date.now(),
                  ...msg,
                  type: MessageType.Recall,
                  canWithdraw: false,
                  beRecalled: true,
                }
              : msg;
          }),
          msgList: state.msgList.map((msg) => {
            if (msg.quoteMessage && msg.quoteMessage.id === messageId) {
              msg.quoteMessage.content = JSON.stringify({
                text: "",
              });
              msg.quoteMessage.hasRedrawn = true;
            }
            return msg.id === messageId
              ? {
                  recallTime: Date.now(),
                  ...msg,
                  beRecalled: true,
                  type: MessageType.Recall,
                  canWithdraw: false,
                }
              : msg;
          }),
        };
      });
    },

    // 重置状态
    reset: () => {
      set({
        historyMsgList: [],
        msgList: [],
        isConnected: SocketConnectionStatus.DISCONNECTED,
        hasUserSentFirstMessage: false,
        imServiceChannel: ImServiceChannel.Robot,
      });
    },

    // 设置socketError信息
    setSocketErrorInfo: (socketErrorInfo: SocketErrorInfo) => {
      set({ socketErrorInfo });
    },

    setSessionInfo: (sessionInfo: ActiveConversation) => {
      set({
        sessionInfo,
        conversationId: sessionInfo.conversationId ?? "",
      });
    },

    // 添加机器人消息
    setRobotMessage: (message: RobotMessage) => {
      set({ msgList: [...get().msgList, message] });
    },

    // 更新机器人消息
    updateRobotMessage: (messageId: string, opts: Partial<RobotMessage>) => {
      set((state) => {
        const msgList = [...state.msgList];
        const messageIndex = msgList.findIndex((msg) => msg.id === messageId);
        // console.log("updateRobotMessage", messageIndex, opts);
        if (messageIndex !== -1) {
          const updatedMessage = {
            ...msgList[messageIndex],
            ...opts,
          };
          msgList[messageIndex] = updatedMessage;
        }
        return { msgList };
      });
    },

    // 设置 AI 打字机效果是否结束
    setAiTypingEnd: (typingEnd: boolean) => {
      set({ aiTypingEnd: typingEnd });
    },

    // 设置全局 SSE 停止函数
    setGlobalStopStreaming: (stopFn: (() => void) | null) => {
      set({ globalStopStreaming: stopFn });
    },

    // 设置 AI SSE 流是否正在进行中
    setIsAIStreaming: (streaming: boolean) => {
      set({ isAIStreaming: streaming });
    },
  };
};

export const useMessageStore = create<MessageStore>()(
  devtools(subscribeWithSelector(createMessageStore), {
    name: "MessageStore",
    enabled: true,
    serialize: true,
  } as DevtoolsOptions),
);

/**
 * 订阅 socketErrorInfo 变化，自动推送一条机器人消息通知用户
 * 这样 socketClient 只需要调用 setSocketErrorInfo，无需关心消息推送逻辑
 */
useMessageStore.subscribe(
  (state) => state.socketErrorInfo,
  (socketErrorInfo) => {
    if (socketErrorInfo?.message) {
      useMessageStore.getState().setRobotMessage({
        type: MessageType.RichText,
        headerType: HeaderType.MESSAGE,
        senderType: MessageSender.SYSTEM,
        messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
        showFeedFooter: false,
        content: socketErrorInfo.message,
        timestamp: Date.now(),
        senderName: "",
        robotData: {},
      });
    }
  },
);

// 消息格式化清洗（XSS预防等）
export function sanitizeMessage(message: Message): Message {
  if (typeof message.content === "string") {
    return {
      ...message,
      content: message.content,
    };
  }
  return message;
}
